import assembly from '../data/250-sky-leviathan-assembly.json' with { type: 'json' };
import {
  skyFlightPointToWorld,
  skyFlightVectorToWorld,
  skyFlightPointToLocal,
} from './92-sky-flight-frame.mjs';
import { skyLeviathanMeshSweep } from './96-sky-leviathan-contacts.mjs';
const p = (x, y, z) => ({ x, y, z });
const finiteVector = (v) =>
  Array.isArray(v) && v.length === 3 && v.every((n) => Number.isFinite(n) && Math.abs(n) <= 150);
const matrix = (m) => Array.isArray(m) && m.length === 16 && m.every(Number.isFinite);
const pointArray = ({ x, y, z }) => [x, y, z];
const deepFreeze = (value) => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
};
function validAssembly(value) {
  if (
    value?.schemaVersion !== 1 ||
    !/^[a-f0-9]{64}$/.test(value.assetSha256) ||
    !Array.isArray(value.armorParts) ||
    value.armorParts.length !== 7 ||
    !Array.isArray(value.fragments) ||
    value.fragments.length !== 4 ||
    !Number.isFinite(value.crash?.roll) ||
    Math.abs(value.crash.roll) > 0.5 ||
    ![value.crash.finPortRadians, value.crash.finStarboardRadians].every(
      (angle) => Number.isFinite(angle) && Math.abs(angle) <= Math.PI,
    ) ||
    !Array.isArray(value.crash.supports) ||
    value.crash.supports.length < 2 ||
    value.crash.supports.length > 256
  )
    return false;
  const ids = new Set(value.armorParts.map((part) => part.id));
  if (
    ids.size !== 7 ||
    !value.armorParts.every(
      (part) =>
        /^[a-z][a-z0-9_]+$/.test(part.id) &&
        typeof part.mesh === 'string' &&
        typeof part.node === 'string' &&
        finiteVector(part.hinge) &&
        finiteVector(part.axis) &&
        matrix(part.pivotToHull) &&
        matrix(part.meshToHull) &&
        (part.parent === null || ids.has(part.parent)),
    )
  )
    return false;
  for (const part of value.armorParts) {
    const seen = new Set([part.id]);
    let parent = part.parent;
    while (parent) {
      if (seen.has(parent)) return false;
      seen.add(parent);
      parent = value.armorParts.find((p) => p.id === parent).parent;
    }
  }
  return (
    value.crash.supports.every((point) => finiteVector(pointArray(point))) &&
    new Set(value.crash.supports.map((point) => pointArray(point).join(','))).size ===
      value.crash.supports.length &&
    value.fragments.every(
      (part) =>
        typeof part.id === 'string' &&
        finiteVector(part.hinge) &&
        finiteVector(part.axis) &&
        Math.abs(Math.hypot(...part.axis) - 1) < 1e-8 &&
        Number.isFinite(part.openRadians) &&
        Math.abs(part.openRadians) <= Math.PI &&
        Array.isArray(part.vertices) &&
        part.vertices.length <= 512 &&
        part.vertices.every(finiteVector) &&
        Array.isArray(part.triangles) &&
        part.triangles.length <= 512 &&
        part.triangles.every(
          (t) =>
            Array.isArray(t) &&
            t.length === 3 &&
            t.every((i) => Number.isSafeInteger(i) && i >= 0 && i < part.vertices.length),
        ),
    )
  );
}
if (!validAssembly(assembly)) throw new TypeError('Invalid pinned Leviathan assembly geometry');
export const SKY_LEVIATHAN_ASSEMBLY = deepFreeze(assembly);
const fragments = assembly.fragments.map((part) => {
  const vertices = part.vertices.map((v) =>
    Object.freeze(p(...v.map((n, axis) => n - part.hinge[axis]))),
  );
  return Object.freeze({
    ...part,
    radius: Math.max(...vertices.map((v) => Math.hypot(v.x, v.y, v.z))),
    hinge: Object.freeze(p(...part.hinge)),
    axis: Object.freeze(p(...part.axis)),
    triangles: Object.freeze(
      part.triangles.map(([a, b, c], index) =>
        Object.freeze({
          id: `${part.id}-${index}`,
          a: vertices[a],
          b: vertices[b],
          c: vertices[c],
        }),
      ),
    ),
  });
});

/** The exact visible shell leaves rotate around their measured hinges.
 * Returned contact frames map immutable hinge-local triangles into world
 * metres; omitting hullFrame produces hull-local frames for pilot sweeps. */
export function skyLeviathanRupture(
  systems,
  hullFrame = {
    origin: p(0, 0, 0),
    right: p(1, 0, 0),
    up: p(0, 1, 0),
    back: p(0, 0, 1),
  },
) {
  const fraction = Math.max(0, Math.min(1, systems?.breach ?? 0));
  return fragments.map((part) => {
    const angle = part.openRadians * fraction,
      c = Math.cos(angle),
      s = Math.sin(angle),
      a = part.axis;
    const rotate = (v) => {
      const dot = a.x * v.x + a.y * v.y + a.z * v.z;
      return p(
        v.x * c + (a.y * v.z - a.z * v.y) * s + a.x * dot * (1 - c),
        v.y * c + (a.z * v.x - a.x * v.z) * s + a.y * dot * (1 - c),
        v.z * c + (a.x * v.y - a.y * v.x) * s + a.z * dot * (1 - c),
      );
    };
    const contactFrame = {
      origin: skyFlightPointToWorld(hullFrame, part.hinge),
      right: skyFlightVectorToWorld(hullFrame, rotate(p(1, 0, 0))),
      up: skyFlightVectorToWorld(hullFrame, rotate(p(0, 1, 0))),
      back: skyFlightVectorToWorld(hullFrame, rotate(p(0, 0, 1))),
    };
    return { ...part, angle, contactFrame };
  });
}

/** Hull-local pilot sweep against the same opening shell leaves. Protected
 * transit waits for full opening; this catches any real residual obstruction. */
export function skyLeviathanRuptureSweep(from, to, systems, radius) {
  let first = null;
  for (const part of skyLeviathanRupture(systems)) {
    const hit = skyLeviathanMeshSweep(
      skyFlightPointToLocal(part.contactFrame, from),
      skyFlightPointToLocal(part.contactFrame, to),
      part.triangles,
      radius,
    );
    if (hit && (!first || hit.fraction < first.fraction))
      first = {
        ...hit,
        point: skyFlightPointToWorld(part.contactFrame, hit.point),
      };
  }
  return first;
}
